import { useEffect, useState } from 'react';
import styled from 'styled-components';
import SearchResultsTypeAhead from './SearchResultsTypeAhead';
import { device } from '../../../utils/styled-component-mixins';
import useMediaQuery from '../../custom-hooks/useMediaQuery';
import ButtonLink from '../../shared/ButtonLinkComponent';
import { useLocalization } from '../../custom-hooks/useLocalization';
import { withLocalization } from '../../shared/Context/LocalizationContext';
import SearchFilters from './SearchFilters';
import SearchResults from './SearchResults';
import Pagination from '../../shared/Pagination';
import ServerStylesConditionalWrapper from '../../shared/ServerStylesConditionalWrapper';
import BdoSelectInputContainer from '../../shared/BdoSelectInput/BdoSelectInputContainer';
import SearchResultsPageStyled from './SearchResultsPageStyled';
import MobileSortMenu from './MobileSortMenu';
import { SEARCH_QUERY_PARAMS } from '../../utils/search-query-param-constants';

const SearchBdoSelectInputContainer = styled(BdoSelectInputContainer)`
  .select-input-wrapper {
    margin-bottom: 0;

    label,
    .select-selected {
      padding-bottom: 0.8rem;
    }
  }

  div[class^='BdoSelectInput'] {
    position: static;

    .select-wrapper {
      position: relative;

      .options.show {
        grid-template-columns: auto;
        width: 100%;
      }

      .options {
        li {
          &:first-child {
            color: var(--cta-blue);
            cursor: pointer;
          }

          &[aria-selected='true'] {
            color: var(--charcoal-2);
            cursor: default;
          }

          &:hover:not([aria-selected='true']) {
            font-weight: 700;
          }

          .close-icon {
            display: none;
          }
        }
      }
    }
  }
`;

const SiteSearchPage = () => {
  const { resourceStrings } = useLocalization();
  const isDesktop = useMediaQuery(device.desktop);
  const [isFiltersShown, setIsFiltersShown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentResults, setCurrentResults] = useState([]);
  const [filtersWithCount, setFiltersWithCount] = useState([]);
  const [totalResults, setTotalResults] = useState([]);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [formattedResultsText, setFormattedResultsText] = useState('');
  const [sortBy, setSortBy] = useState(0);
  const [showMobileSortMenu, setShowMobileSortMenu] = useState(false);
  const [isGridView, setIsGridView] = useState(true);
  const [userSelectedSortState, setUserSelectedSortState] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const servicesFilterText = resourceStrings.searchServicesFilterText;
  const industriesFilterText = resourceStrings.searchIndustriesFilterText;
  const insightsFilterText = resourceStrings.searchInsightsFilterText;
  const hideFiltersButtonText = resourceStrings.searchHideFiltersButtonText;
  const showFiltersButtonText = resourceStrings.searchShowFiltersButtonText;
  const clearFiltersButtonText = resourceStrings.searchClearFiltersButtonText;
  const mobileFiltersLabel = resourceStrings.searchMobileFilterLabel;
  const insightReadMoreText = resourceStrings.featuredInsightLinkText;
  const resultsCountText = resourceStrings.searchResultsCountText;
  const sortLabelText = resourceStrings.searchSortLabelText;
  const sortRecentOptionText = resourceStrings.searchSortRecentOptionText;
  const sortTopResultsOptionText = resourceStrings.searchSortTopResultsOptionText;
  const showingAllResultsText = resourceStrings.searchShowingAllResultsText;
  const layoutViewText = resourceStrings.searchLayoutViewText;
  const sortLabelTextMobile = resourceStrings.searchSortLabelTextMobile;
  const seeResultsTextMobile = resourceStrings.searchMobileSeeResultsText;

  const { SEARCH_TEXT, FILTER, SORT } = SEARCH_QUERY_PARAMS;

  const filters = [
    {
      codeName: 'BDO.ServiceNode',
      displayName: servicesFilterText
    },
    {
      codeName: 'BDO.IndustryNode',
      displayName: industriesFilterText
    },
    {
      codeName: 'BDO.InsightNode',
      displayName: insightsFilterText
    }
  ];

  const sortByMapping = {
    0: 'recent',
    1: 'top-results'
  };

  const getUrlParams = () => {
    const queryString = window.location.search;
    return new URLSearchParams(queryString);
  };

  const getSearchString = () => {
    const searchString = getUrlParams().get(SEARCH_TEXT) ? getUrlParams().get(SEARCH_TEXT) : '';

    return searchString;
  };

  const formatResultsTextString = (resultsCount, string) => string?.replace('{0}', resultsCount);

  const setFilterCounts = (facets) => {
    const filtersWithCountTemp = [];
    const facetValues = facets?.find((facet) => facet.name === 'classname')?.values;
    filters.forEach((filter) => {
      const facetMatch = facetValues?.find((facetValue) => filter.codeName === facetValue.value);
      const count = facetMatch?.count ? facetMatch?.count : 0;
      // eslint-disable-next-line no-param-reassign
      filter = { ...filter, count };
      filtersWithCountTemp.push(filter);
    });
    setFiltersWithCount(filtersWithCountTemp);
  };

  const updateUrl = (newSearchString = false, newType = false, newSortBy = false) => {
    const urlParams = getUrlParams();
    const currentSearchString = urlParams.get(SEARCH_TEXT) ? urlParams.get(SEARCH_TEXT) : '';
    const currentTypeFilter = urlParams.get(FILTER) ? urlParams.get(FILTER) : '';
    const currentSortBy = urlParams.get(SORT) ? urlParams.get(SORT) : sortByMapping[0];

    const updatedSearchString = newSearchString !== false ? newSearchString : currentSearchString;
    const updatedTypeFilter = newType !== false ? newType : currentTypeFilter;
    const updatedSortBy = newSortBy !== false ? sortByMapping[newSortBy] : currentSortBy;

    const newUrl = `/search?${SEARCH_TEXT}=${encodeURIComponent(
      updatedSearchString
    )}&${FILTER}=${updatedTypeFilter}&${SORT}=${updatedSortBy}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
  };

  const fetchSearchResults = (pageNumber = 1, userSelectedSort) => {
    setIsLoading(true);
    const searchString = getSearchString();
    const typeFilter = getUrlParams().get(FILTER) ? getUrlParams().get(FILTER) : '';
    let sortByValue = getUrlParams().get(SORT) ? getUrlParams().get(SORT) : sortByMapping[0];

    if (!isFirstRender && !userSelectedSortState && !userSelectedSort) {
      sortByValue = sortByMapping['1'];
      setSortBy(1);
      updateUrl(false, false, 1);
    }

    if (userSelectedSortState || userSelectedSort) {
      sortByValue = getUrlParams().get(SORT);
    }

    fetch(
      `/api/search/12/${pageNumber}?searchstring=${encodeURIComponent(
        searchString
      )}&type=${typeFilter}&sortby=${sortByValue}`
    )
      .then((response) => response.json())
      .then((data) => {
        setSearchTerm(searchString);
        setCurrentResults(data.results);
        setFilterCounts(data.searchFacets);
        setFormattedResultsText(formatResultsTextString(data.totalCount, resultsCountText));
        setNumberOfPages(Math.ceil(data.totalCount / 12));
        setCurrentPage(pageNumber);
        setTotalResults(data.totalCount);
        setIsLoading(false);
      })
      .catch((error) => console.log(error));
  };

  const sortByOptions = [sortRecentOptionText, sortTopResultsOptionText];

  const handleSortChange = (option) => {
    setSortBy(sortByOptions.lastIndexOf(option));
    setUserSelectedSortState(true);
    updateUrl(false, false, sortByOptions.lastIndexOf(option));
    fetchSearchResults(1, true);
  };

  const sortFilters = [
    {
      filterOptions: sortByOptions,
      onChange: handleSortChange,
      preselectedOption: sortBy
    }
  ];

  const updateSelectedFilter = (filterCodeName) => {
    setSelectedFilter(filterCodeName);
    updateUrl(false, filterCodeName);
    fetchSearchResults(1);
  };

  const clearFilters = () => {
    updateUrl(false, '');
    setSelectedFilter('');
    fetchSearchResults(1);
  };

  useEffect(() => {
    if (isDesktop) {
      setIsFiltersShown(true);
    } else {
      setIsFiltersShown(false);
    }

    const preSelectedFilter = getUrlParams().get(FILTER);
    const preSelectedSortBy = getUrlParams().get(SORT);

    setSelectedFilter(preSelectedFilter);
    setSortBy(
      Object.keys(sortByMapping).find((key) => sortByMapping[key] === preSelectedSortBy) || 0
    );

    if (isFirstRender) {
      fetchSearchResults(1, true);
    }
    setIsFirstRender(false);
  }, [isDesktop]);

  const handleEnterOnSearchTypeAhead = (updatedSearchTerm) => {
    setSearchTerm(updatedSearchTerm);
    fetchSearchResults();
  };

  return (
    <ServerStylesConditionalWrapper>
      <SearchResultsPageStyled className="container container-padding-full-width">
        <div className="search-controls-container">
          <div className="row">
            <div className="col-lg-3"></div>
            <div className="col-lg-9">
              <SearchResultsTypeAhead
                searchPageTypeAheadEnterHandler={handleEnterOnSearchTypeAhead}
              />
            </div>
          </div>
          <div className="row">
            {isDesktop ? (
              // desktop filter, sort, and view controls
              <>
                <div className="col-md-3 filter-buttons">
                  <ButtonLink
                    text={isFiltersShown ? hideFiltersButtonText : showFiltersButtonText}
                    type="icon-tertiary"
                    icon={isFiltersShown ? 'visibility_off' : 'visibility'}
                    isButtonTag={true}
                    onClickFunction={() => setIsFiltersShown(!isFiltersShown)}
                    tertiaryIconHasBackground={false}
                    useWidgetIdWrapper={false}
                    ariaAttributes={{
                      'aria-controls': 'search-filters'
                    }}
                  />
                  {isFiltersShown && selectedFilter && (
                    <ButtonLink
                      className="clear-filters-btn-desktop"
                      isButtonTag={true}
                      onClickFunction={() => clearFilters()}
                      text={clearFiltersButtonText}
                      tertiaryIconHasBackground={false}
                      type="plain-link"
                      useWidgetIdWrapper={false}
                      ariaAttributes={{
                        'aria-controls': 'search-filters'
                      }}
                    />
                  )}
                </div>
                <div className="col-md-9 results-controls">
                  {currentResults && !isLoading && (
                    <p aria-live="polite">
                      {searchTerm ? (
                        <>
                          {formattedResultsText} <b>"{searchTerm}"</b>
                        </>
                      ) : (
                        <>{showingAllResultsText}</>
                      )}
                    </p>
                  )}
                  <div className="search-sort-view-controls">
                    <SearchBdoSelectInputContainer
                      className="bdo-select-container"
                      filterLabel={sortLabelText}
                      filters={sortFilters}
                      updatedValueFromParent={sortBy}
                    />
                    <fieldset>
                      <div className="view-controls">
                        <legend>{layoutViewText}</legend>
                        <div>
                          <input
                            aria-controls="search-results"
                            className="list-view-control"
                            type="radio"
                            name="layout"
                            id="list-view"
                            value="list"
                            onChange={() => setIsGridView(false)}
                            checked={!isGridView}
                          />
                          <label htmlFor="list-view" aria-label="list view"></label>
                        </div>
                        <div>
                          <input
                            aria-controls="search-results"
                            className="grid-view-control"
                            type="radio"
                            name="layout"
                            id="grid-view"
                            value="grid"
                            onChange={() => setIsGridView(true)}
                            checked={isGridView}
                          />
                          <label htmlFor="grid-view" aria-label="grid view"></label>
                        </div>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </>
            ) : (
              // Mobile filter and sort controls
              <>
                <div className="col-12 mobile-filter-sort-controls">
                  <ButtonLink
                    text={isFiltersShown ? hideFiltersButtonText : showFiltersButtonText}
                    type="icon-tertiary"
                    icon="tune"
                    isButtonTag={true}
                    onClickFunction={() => setIsFiltersShown(!isFiltersShown)}
                    tertiaryIconHasBackground={false}
                    useWidgetIdWrapper={false}
                    ariaAttributes={{
                      'aria-controls': 'search-filters'
                    }}
                  />

                  <ButtonLink
                    text={sortLabelTextMobile}
                    type="tertiary"
                    isButtonTag={true}
                    onClickFunction={() => setShowMobileSortMenu(!showMobileSortMenu)}
                    tertiaryIconHasBackground={false}
                    useWidgetIdWrapper={false}
                    replacementTertiaryIcon="sort"
                    ariaAttributes={{
                      'aria-controls': 'search-sortby'
                    }}
                  />
                </div>
                <div className="col-12">
                  {currentResults && !isLoading && (
                    <p>
                      {searchTerm ? (
                        <>
                          {formattedResultsText} <b>"{searchTerm}"</b>
                        </>
                      ) : (
                        <>{showingAllResultsText}</>
                      )}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="row search-filters-results">
          {isDesktop ? (
            <>
              {isFiltersShown && (
                <SearchFilters
                  isFiltersShown={isFiltersShown}
                  filters={filtersWithCount}
                  updateSelectedFilter={updateSelectedFilter}
                  selectedFilter={selectedFilter}
                  setIsFiltersShown={setIsFiltersShown}
                />
              )}
            </>
          ) : (
            <>
              <MobileSortMenu
                showMobileSortMenu={showMobileSortMenu}
                sortFilters={sortFilters}
                setSortBy={setSortBy}
                sortBy={sortBy}
                setShowMobileSortMenu={setShowMobileSortMenu}
                sortFiltersLabel={sortLabelTextMobile}
              />
              <SearchFilters
                isFiltersShown={isFiltersShown}
                filters={filtersWithCount}
                updateSelectedFilter={updateSelectedFilter}
                selectedFilter={selectedFilter}
                setIsFiltersShown={setIsFiltersShown}
                clearFiltersButtonText={clearFiltersButtonText}
                clearFiltersFunction={clearFilters}
                mobileFiltersLabel={mobileFiltersLabel}
                seeResultsTextMobile={seeResultsTextMobile}
                totalResults={totalResults}
              />
            </>
          )}
          <div className={isFiltersShown ? 'col-md-9' : 'col-md-12'}>
            <SearchResults
              insightReadMoreText={insightReadMoreText}
              isLoading={isLoading}
              results={currentResults}
              isGridView={isGridView}
            />
            {numberOfPages > 1 && (
              <Pagination
                fetchPageData={fetchSearchResults}
                numberOfPages={numberOfPages}
                currentPage={currentPage}
              />
            )}
          </div>
        </div>
      </SearchResultsPageStyled>
    </ServerStylesConditionalWrapper>
  );
};

export default withLocalization(SiteSearchPage);
